import React, { useState, useEffect } from 'react';
import avevaApi from '@services/avevaApiService';

interface AvevaStreamDetailsProps {
  streamId: string;
  onBack: () => void;
}

interface StreamInfo {
  Id: string;
  TypeId?: string;
  Name?: string;
  Description?: string;
}

interface StreamDataItem {
  Time?: string;
  Timestamp?: string;
  Value?: any;
  Measurement?: any;
  Quality?: string;
}

const AvevaStreamDetails = ({ streamId, onBack }: AvevaStreamDetailsProps) => {
  const [streamInfo, setStreamInfo] = useState<StreamInfo | null>(null);
  const [streamData, setStreamData] = useState<StreamDataItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [activeDataTab, setActiveDataTab] = useState<'latest' | 'historical'>('latest');

  useEffect(() => {
    loadStreamDetails();
  }, [streamId]);

  const loadStreamDetails = async () => {
    setLoading(true);
    try {
      const [streamResponse, latestResponse] = await Promise.all([
        avevaApi.getStream(streamId),
        avevaApi.getLatestValue(streamId),
      ]);
      setStreamInfo(streamResponse.data);
      setStreamData([latestResponse.data]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadHistoricalData = async () => {
    setDataLoading(true);
    try {
      const endTime = new Date().toISOString();
      const startTime = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const response = await avevaApi.getStreamData(streamId, {
        startIndex: startTime,
        endIndex: endTime,
        count: 100,
      });
      setStreamData(response.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDataLoading(false);
    }
  };

  if (loading) return <div className="loading" data-testid="aveva-stream-details-loading">Loading stream details...</div>;

  if (error)
    return (
      <div className="stream-details" data-testid="aveva-stream-details" data-stream-id={streamId}>
        <button className="back-btn" onClick={onBack} data-testid="aveva-back-button">
          ← Back
        </button>
        <div className="error" data-testid="aveva-stream-details-error">Error: {error}</div>
      </div>
    );

  return (
    <div className="stream-details" data-testid="aveva-stream-details" data-stream-id={streamId}>
      <div className="details-header">
        <button className="back-btn" onClick={onBack} data-testid="aveva-back-button">
          ← Back
        </button>
        <h2>Stream Details: {streamId}</h2>
      </div>

      {streamInfo && (
        <div className="stream-info" data-testid="aveva-stream-info">
          <h3>Stream Information</h3>
          <div className="info-grid">
            <div>
              <strong>ID:</strong> {streamInfo.Id}
            </div>
            <div>
              <strong>Type:</strong> {streamInfo.TypeId || 'N/A'}
            </div>
            <div>
              <strong>Name:</strong> {streamInfo.Name || 'N/A'}
            </div>
            <div>
              <strong>Description:</strong> {streamInfo.Description || 'N/A'}
            </div>
          </div>
        </div>
      )}

      <div className="stream-data">
        <div className="data-header">
          <h3>Stream Data</h3>
          <div className="data-tabs">
            <button
              className={`tab-btn ${activeDataTab === 'latest' ? 'active' : ''}`}
              onClick={() => setActiveDataTab('latest')}
              data-testid="aveva-data-tab-button"
              data-tab="latest"
              data-active={String(activeDataTab === 'latest')}
            >
              Latest Value
            </button>
            <button
              className={`tab-btn ${activeDataTab === 'historical' ? 'active' : ''}`}
              data-testid="aveva-data-tab-button"
              data-tab="historical"
              data-active={String(activeDataTab === 'historical')}
              onClick={() => {
                setActiveDataTab('historical');
                if (streamData.length <= 1) loadHistoricalData();
              }}
            >
              Historical Data
            </button>
          </div>
        </div>

        {dataLoading && <div className="loading" data-testid="aveva-stream-data-loading">Loading data...</div>}

        {!dataLoading && streamData.length > 0 && (
          <div className="data-content">
            {activeDataTab === 'latest' ? (
              <pre data-testid="aveva-latest-value">{JSON.stringify(streamData[0], null, 2)}</pre>
            ) : (
              <table data-testid="aveva-historical-table" data-row-count={String(streamData.length)}>
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Value</th>
                    <th>Quality</th>
                  </tr>
                </thead>
                <tbody>
                  {streamData.map((item) => {
                    const time = item.Time || item.Timestamp || '';
                    const value = item.Value || item.Measurement || '';

                    const key = `${time}-${value}`;

                    return (
                      <tr key={key} data-testid="aveva-historical-row">
                        <td>{new Date(time).toLocaleString()}</td>
                        <td>{JSON.stringify(value || 'N/A')}</td>
                        <td>{item.Quality || 'N/A'}</td>
                      </tr>
                    );
                  })}

                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="data-actions">
          <button onClick={loadStreamDetails} data-testid="aveva-refresh-latest-button">Refresh Latest</button>
          <button onClick={loadHistoricalData} data-testid="aveva-load-historical-button">Load Historical</button>
        </div>
      </div>
    </div>
  );
};

export default AvevaStreamDetails;
